import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModeToggle, useThemeStore, defaultThemeIdForPersona } from '@kidshome/ui';
import { LanguageToggle } from '@/shared/ui/LanguageToggle';

/**
 * Shared full-page shell for the pre-auth screens (login, forgot/reset password, accept-invite, verify
 * email — all guardian-facing; kids sign in via the separate PIN `KidLoginPage`). Centres a single card
 * under a branded header (app name + language / light-dark toggles) — the same chrome each of these
 * public pages used to inline verbatim. The family experience has a single theme (warm-neutral teal), so
 * there is no theme picker here — only the light/dark mode toggle. The page supplies its own `<Card>`
 * (or other content) as `children`; everything around it lives here.
 *
 * It SCOPES the family theme while mounted (mirroring `AppShell` for the family persona), so the pre-auth
 * pages always render warm-neutral teal even on a device whose last user was a kid (whose cozy-pastel
 * `themeId` would otherwise persist onto the login screen). Cleared on unmount → the post-login shell
 * takes over. Only the THEME is scoped; the light/dark mode stays the user's choice.
 *
 * Callers must already load the `common` + `settings` i18n namespaces (every auth page does). Imported
 * by its direct path (`@/shared/ui/AuthPageShell`), not the `shared/ui` barrel.
 */
export function AuthPageShell({ children }: { children: React.ReactNode }): React.ReactElement {
  const { t } = useTranslation(['common', 'settings']);
  const applyScopedTheme = useThemeStore((s) => s.applyScopedTheme);
  const clearScopedTheme = useThemeStore((s) => s.clearScopedTheme);

  useEffect(() => {
    applyScopedTheme(defaultThemeIdForPersona('family'));
    return () => clearScopedTheme();
  }, [applyScopedTheme, clearScopedTheme]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-bg p-6">
      <div className="flex w-full max-w-sm items-center justify-between">
        <span className="font-display text-lg font-bold text-text">{t('common:app.name')}</span>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <ModeToggle label={t('settings:appearance.toggleMode')} />
        </div>
      </div>

      {children}
    </div>
  );
}
